/* eslint-disable max-lines */
import {
  InteractionEvent,
  MfaFactor,
  SignInIdentifier,
  type VerificationCodeIdentifier,
} from '@logto/schemas';
import { HTTPError } from 'ky';
import {
  type ChangeEvent,
  type CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';
import { validate } from 'superstruct';

import SecondaryPageLayout from '@/Layout/SecondaryPageLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import {
  submitInteraction,
  startWhatsAppReverseMfa,
  getWhatsAppReverseMfaStatus,
  identifyAndSubmitInteraction,
} from '@/apis/experience';
import { initInteraction } from '@/apis/experience/interaction';
import { verifyTrustedDevice } from '@/apis/experience/trusted-device';
import {
  getVerificationSetupStatus,
  getWhatsAppConfig,
} from '@/apis/experience/verification-setup';
import { getInteractionEventFromState, sendVerificationCodeApi } from '@/apis/utils';
import Button from '@/components/Button';
import MfaVerifyButton from '@/components/Button/MfaVerifyButton';
import { InputField } from '@/components/InputFields';
import QrCode from '@/components/QrCode';
import TextLink from '@/components/TextLink';
import useApi from '@/hooks/use-api';
import useCachedUserData from '@/hooks/use-cached-user-data';
import useChooseMfa from '@/hooks/use-choose-mfa';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import usePlatform from '@/hooks/use-platform';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import useStartTotpBinding from '@/hooks/use-start-totp-binding';
import useStartWebAuthnProcessing from '@/hooks/use-start-webauthn-processing';
import useToast from '@/hooks/use-toast';
import { UserFlow, UserMfaFlow } from '@/types';
import { mfaFactors, type MfaFlowState } from '@/types/guard';

import styles from './index.module.scss';

const override: CSSProperties = {
  display: 'block',
  margin: '0 auto',
  borderColor: 'black',
};

/** Default OIDC interaction (sign-in session) TTL in seconds — matches the backend default. */
const defaultInteractionTtlSeconds = 3600;
/** Show the countdown in a warning style once this little time remains. */
const countdownWarningMs = 60_000;

const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};
type MfaOption = {
  factor: MfaFactor;
  name: string;
  description: string;
};

const MfaChoose = () => {
  const { t } = useTranslation();
  const { experienceSettings } = useContext(PageContext);
  // Desktop (PC/laptop) shows a QR to scan with the phone that has WhatsApp; mobile/tablet shows a
  // tap-to-open button since the wa.me deep link opens the app directly. `isMobile` is true for
  // phones and tablets (react-device-detect), false for desktop.
  const { isMobile } = usePlatform();
  const asyncSendVerificationCode = useApi(sendVerificationCodeApi);
  const asyncGetSetupStatus = useApi(getVerificationSetupStatus);
  const asyncInitInteraction = useApi(initInteraction);
  const asyncVerifyTrustedDevice = useApi(verifyTrustedDevice);
  const asyncSubmitInteraction = useApi(submitInteraction);
  const redirectTo = useGlobalRedirectTo();
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const handleError = useErrorHandler();
  const { setToast } = useToast();
  const navigate = useNavigate();
  const { state } = useLocation();
  const startTotpBinding = useStartTotpBinding();
  const startWebAuthnProcessing = useStartWebAuthnProcessing();
  const { onSubmit, clearErrorMessage } = useSendVerificationCode(UserFlow.SignIn);
  const { handleMfaRedirect } = useChooseMfa();
  const { userData, isLoading, error, fetchUserData, isRateLimited, getRateLimitRetryTime } =
    useCachedUserData();
  const [mfaOptions, setMfaOptions] = useState<MfaOption[]>([]);
  const [phoneInputMode, setPhoneInputMode] = useState<undefined | 'whatsapp' | 'sms'>(null);
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);

  // Sign-in session countdown: how long until the OIDC interaction expires. Derived from the
  // configurable `mfa.interactionTtlSeconds` (default 3600). When it hits zero the interaction is
  // dead, so proactively send the user back to sign in — matching the `session.not_found` redirect.
  const [sessionDeadline, setSessionDeadline] = useState<number>();
  const [sessionRemainingMs, setSessionRemainingMs] = useState<number>();

  useEffect(() => {
    if (sessionDeadline === undefined && experienceSettings) {
      const ttlSeconds =
        experienceSettings.mfa?.interactionTtlSeconds ?? defaultInteractionTtlSeconds;
      setSessionDeadline(Date.now() + ttlSeconds * 1000);
    }
  }, [experienceSettings, sessionDeadline]);

  useEffect(() => {
    if (sessionDeadline === undefined) {
      return;
    }
    const tick = () => {
      const remaining = sessionDeadline - Date.now();
      setSessionRemainingMs(remaining);
      if (remaining <= 0) {
        // eslint-disable-next-line @silverhand/fp/no-mutation
        window.location.href = '/error?message=session_expired';
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [sessionDeadline]);

  const [color, setColor] = useState('#000000');

  // Reverse-OTP (WhatsApp): when enabled, the user sends an inbound message via a wa.me link
  // instead of receiving a code. Selected by the server flag WHATSAPP_REVERSE_OTP_ENABLED.
  const asyncStartReverse = useApi(startWhatsAppReverseMfa);
  const [reverseEnabled, setReverseEnabled] = useState(false);
  const [reverseWaMeUrl, setReverseWaMeUrl] = useState<string | undefined>(undefined);
  const reversePollTimer = useRef<ReturnType<typeof setInterval>>();
  // F2/F3: surface the link lifetime so the user is never left polling a dead token in silence.
  const [reverseExpired, setReverseExpired] = useState(false);
  const [reverseSecondsLeft, setReverseSecondsLeft] = useState<number>();
  const reverseCountdownTimer = useRef<ReturnType<typeof setInterval>>();
  const stopReverseTimers = useCallback(() => {
    if (reversePollTimer.current) {
      clearInterval(reversePollTimer.current);
    }
    if (reverseCountdownTimer.current) {
      clearInterval(reverseCountdownTimer.current);
    }
  }, []);
  // Hold the config fetch as a promise so the WA handler can await the SAME load instead of
  // reading the `reverseEnabled` state, which may still be its `false` default on first click.
  const reverseConfigPromise = useRef<Promise<boolean>>();

  useEffect(() => () => stopReverseTimers(), [stopReverseTimers]);

  // Reverse-OTP (WhatsApp): start a verification for `phone`, show the wa.me link, and poll until
  // the inbound message verifies it — then identify-and-submit (same downstream as the code flow).
  const startReverseWhatsApp = useCallback(
    async (phone: string) => {
      setIsSubmitting(true);
      const [error, result] = await asyncStartReverse(
        getInteractionEventFromState(state) ?? InteractionEvent.SignIn,
        { type: SignInIdentifier.Phone, value: phone },
        userId ?? ''
      );
      setIsSubmitting(false);

      if (error) {
        await handleError(error);
        return;
      }
      if (!result) {
        return;
      }

      setReverseWaMeUrl(result.waMeUrl);
      setReverseExpired(false);
      setReverseSecondsLeft(result.expiresInSeconds);

      reversePollTimer.current = setInterval(async () => {
        try {
          const status = await getWhatsAppReverseMfaStatus(result.verificationId, userId ?? '');
          if (status.verified) {
            stopReverseTimers();
            const submitResult = await identifyAndSubmitInteraction({
              verificationId: result.verificationId,
            });
            if (submitResult?.redirectTo) {
              await redirectTo(submitResult.redirectTo);
            }
          }
        } catch (pollError) {
          // F3: a 4xx means the link can no longer succeed (e.g. expired, phone in use) — stop polling
          // and surface it instead of looping forever in silence. Transient/5xx errors keep polling.
          if (pollError instanceof HTTPError && pollError.response.status < 500) {
            stopReverseTimers();
            await handleError(pollError);
          }
        }
      }, 2500);

      // F2: count the link down; when it hits zero the token is dead — stop polling and tell the user
      // rather than leaving the page silently spinning.
      reverseCountdownTimer.current = setInterval(() => {
        setReverseSecondsLeft((previous) => {
          const next = (previous ?? 0) - 1;
          if (next <= 0) {
            stopReverseTimers();
            setReverseExpired(true);
            return 0;
          }
          return next;
        });
      }, 1000);
    },
    [asyncStartReverse, state, userId, handleError, redirectTo, stopReverseTimers]
  );
  const generateMfaOptions = useCallback(
    (userFactors: string[], email: string, phone: string, showAll = true) => {
      // Email is gated by the sign-in experience factors like every other option
      const emailEnabled = experienceSettings?.mfa.factors.includes(MfaFactor.Email) ?? false;

      // If user has no MFA factors set up, show binding options + email
      if (userFactors.length === 0) {
        const emailOption: MfaOption = {
          factor: MfaFactor.Email,
          name: 'Email',
          description: 'Email sent to ' + email,
        };

        const totpOption: MfaOption = {
          factor: MfaFactor.TOTP,
          name: 'Set up Authenticator app',
          description: 'Set up TOTP with Google Authenticator, etc.',
        };

        const smsOption: MfaOption = {
          factor: MfaFactor.SMS,
          name: 'SMS',
          description: 'Code sent to your phone ' + phone,
        };

        const waOption: MfaOption = {
          factor: MfaFactor.WA,
          name: 'WhatsApp',
          description: 'Code sent to your WhatsApp',
        };

        const webAuthnOption: MfaOption = {
          factor: MfaFactor.WebAuthn,
          name: 'Set up device passkey',
          description: 'Use biometrics or device PIN (Touch ID, Face ID, Windows Hello, etc.)',
        };

        const options = [
          ...(emailEnabled ? [emailOption] : []),
          ...(experienceSettings?.mfa.factors.includes(MfaFactor.TOTP) ? [totpOption] : []),
          ...(experienceSettings?.mfa.factors.includes(MfaFactor.SMS) && phone ? [smsOption] : []),
          ...(experienceSettings?.mfa.factors.includes(MfaFactor.WA) ? [waOption] : []),
          ...(experienceSettings?.mfa.factors.includes(MfaFactor.WebAuthn) ? [webAuthnOption] : []),
        ];

        // If showAll is false, return only the first enabled option
        if (!showAll) {
          return options.slice(0, 1);
        }

        return options;
      }

      // User has existing factors, show verification options
      const emailOption = {
        factor: MfaFactor.Email,
        name: 'Email',
        description: 'Email sent to ' + email,
      };

      // eslint-disable-next-line no-restricted-syntax
      const additionalOptions = experienceSettings?.mfa.factors
        .map((factor) => {
          switch (factor) {
            case MfaFactor.TOTP: {
              return {
                factor: MfaFactor.TOTP,
                name: 'Authenticator app OTP',
                description: 'E.g., Google Authenticator, etc.',
              };
            }
            case MfaFactor.SMS: {
              return {
                factor: MfaFactor.SMS,
                name: 'SMS',
                description: 'Code sent to your phone ' + phone,
              };
            }
            case MfaFactor.WA: {
              return {
                factor: MfaFactor.WA,
                name: 'WhatsApp',
                description: 'Code sent to your WhatsApp',
              };
            }
            case MfaFactor.WebAuthn: {
              // Always surface Device Security (passkey), just like TOTP. When the
              // user has no passkey yet, selecting it routes through verifying an
              // existing factor first (verify-then-enroll), so the backend
              // `verifiedInteractionGuard` at /profile/mfa stays intact — see
              // `handleMfaSelection` below.
              const registered = userFactors.includes(MfaFactor.WebAuthn);
              return {
                factor: MfaFactor.WebAuthn,
                name: registered ? 'Device Security' : 'Set up Device Security',
                description: registered
                  ? 'Verify with this device (Touch ID, Face ID, Windows Hello, etc.)'
                  : 'Register this device (Touch ID, Face ID, Windows Hello, etc.)',
              };
            }
            default: {
              return null;
            }
          }
        })
        .filter(Boolean) as MfaOption[];

      const options = [...(emailEnabled ? [emailOption] : []), ...additionalOptions];

      // If showAll is false, return only the first enabled option
      if (!showAll) {
        return options.slice(0, 1);
      }

      return options;
    },
    [experienceSettings?.mfa.factors]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userIdParam = params.get('user_id');
    setUserId(userIdParam ?? undefined);

    if (!userIdParam) {
      console.warn('No user ID provided');
      return;
    }

    (async () => {
      setIsSubmitting(true);

      try {
        // 1. FIRST: Initialize interaction session before any other API calls
        await asyncInitInteraction(InteractionEvent.SignIn);

        // 1b. Now that the interaction session exists, fetch the WhatsApp reverse-OTP config.
        // This endpoint is session-guarded — fetching it before init returns 404 and would
        // wrongly cache `false`, sending WhatsApp selections down the forward-OTP code path.
        reverseConfigPromise.current = getWhatsAppConfig()
          .then((config) => config.reverseOtpEnabled)
          .catch(() => false);
        void reverseConfigPromise.current.then(setReverseEnabled);

        // 2. SECOND: Fetch user data (now that interaction is initialized)
        await fetchUserData(userIdParam, true); // Skip initInteraction since we already called it

        // 3. TMMIN MFA — if a trusted-device cookie is present and valid for
        // this user, skip the factor picker and submit the interaction.
        const [verifyError, verifyResult] = await asyncVerifyTrustedDevice(userIdParam);
        if (!verifyError && verifyResult?.valid) {
          const [submitError, submitResult] = await asyncSubmitInteraction();
          if (!submitError && submitResult) {
            await redirectTo(submitResult.redirectTo);
            return;
          }
          console.warn('Trusted-device auto-submit failed, falling back to picker', submitError);
        }

        // TEMPORARILY DISABLED: verification setup redirect
        // const setupCheckKey = `setup_check_${userIdParam}`;
        // const lastCheck = sessionStorage.getItem(setupCheckKey);
        // const now = Date.now();
        // if (!lastCheck || now - Number.parseInt(lastCheck, 10) > 30_000) {
        //   const [setupError, setupStatus] = await asyncGetSetupStatus(userIdParam);
        //   if (!setupError && setupStatus) {
        //     sessionStorage.setItem(setupCheckKey, String(now));
        //     if (setupStatus.setupRequired && !setupStatus.setupCompleted) {
        //       navigate(`/verification-setup?user_id=${userIdParam}`, { replace: true });
        //     }
        //   }
        // }
      } catch (error) {
        console.warn('Error during user/setup flow:', error);

        // Handle session not found errors - redirect to start fresh
        if (
          error instanceof Error &&
          (error.message.includes('session not found') ||
            error.message.includes('Session not found') ||
            error.message.includes('interaction session id cookie not found'))
        ) {
          console.error('Session expired during setup flow! Redirecting to error page...');
          // eslint-disable-next-line @silverhand/fp/no-mutation
          window.location.href = '/error?message=session_expired';
          return;
        }

        // Only handle other errors if it's not a rate limit error
        // Rate limit errors are handled by the UI display
        if (error instanceof Error && !error.message.includes('Too many attempts')) {
          await handleError(error);
        }
      } finally {
        setIsSubmitting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dependencies to run only once on mount

  // Separate effect to update MFA options when user data changes
  useEffect(() => {
    const updatedMfaOptions = generateMfaOptions(
      userData?.availableFactors ?? [],
      userData?.email ?? '',
      userData?.phone ?? '',
      showAllOptions
    );
    setMfaOptions(updatedMfaOptions);
  }, [userData, showAllOptions, generateMfaOptions]);

  const interactionEvent = useMemo<InteractionEvent>(() => {
    const interactionEvent = getInteractionEventFromState(state);
    return interactionEvent ?? InteractionEvent.SignIn;
  }, [state]);

  const handleMfaSelection = useCallback(
    async (option: MfaOption) => {
      switch (option.factor) {
        case MfaFactor.Email: {
          setIsSubmitting(true);
          const identifier: VerificationCodeIdentifier = {
            type: SignInIdentifier.Email,
            value: '',
          };
          await onSubmit({ identifier: SignInIdentifier.Email, value: '', userId });

          setIsSubmitting(false);

          break;
        }
        case MfaFactor.SMS: {
          if (!userData?.phone) {
            setPhoneValue('');
            setPhoneError(undefined);
            setPhoneInputMode('sms');
            break;
          }
          setIsSubmitting(true);
          await onSubmit({ identifier: SignInIdentifier.Phone, value: '', userId });

          setIsSubmitting(false);

          break;
        }
        case MfaFactor.WA: {
          if (!userData?.phone) {
            setPhoneValue('');
            setPhoneError(undefined);
            setPhoneInputMode('whatsapp');
            break;
          }
          // Reverse-OTP: the user already has a registered phone. Open a wa.me link and poll for the
          // inbound message instead of triggering a billable connector send (the forward path below
          // calls the AdMedia connector). Selected by the server flag WHATSAPP_REVERSE_OTP_ENABLED.
          // Await the config load (not the `reverseEnabled` state) so a fast first click can't race
          // ahead of it and wrongly fall through to the forward-OTP code page.
          const isReverse = await (reverseConfigPromise.current ??
            getWhatsAppConfig()
              .then((config) => config.reverseOtpEnabled)
              .catch(() => false));
          if (isReverse) {
            await startReverseWhatsApp(userData.phone);
            break;
          }
          setIsSubmitting(true);
          await onSubmit({
            identifier: SignInIdentifier.Phone,
            value: '',
            userId,
            channel: 'whatsapp',
          });
          setIsSubmitting(false);
          break;
        }
        case MfaFactor.TOTP: {
          const factors = userData?.availableFactors ?? [];
          const hasTotp = factors.includes(MfaFactor.TOTP);

          if (hasTotp) {
            // TOTP registered - verify with it only. Don't pass the full
            // availableFactors list — handleMfaRedirect picks factors[0]
            // (last-used) which may route to passkey instead of TOTP.
            await handleMfaRedirect(UserMfaFlow.MfaVerification, {
              availableFactors: [MfaFactor.TOTP],
              skippable: false,
            });
            break;
          }

          if (factors.includes(MfaFactor.WebAuthn)) {
            // No TOTP but a passkey exists → verify-then-enroll, mirroring the
            // WebAuthn branch below: verify the passkey first (satisfies the
            // backend `verifiedInteractionGuard`), then resume TOTP binding
            // (handled in use-send-mfa-payload via pendingTotpEnrollment).
            await startWebAuthnProcessing(
              UserMfaFlow.MfaVerification,
              {
                availableFactors: [MfaFactor.WebAuthn],
                skippable: false,
                pendingTotpEnrollment: true,
              },
              false,
              userId
            );
            break;
          }

          // No MFA factors at all - start TOTP binding directly.
          const flowState: MfaFlowState = {
            availableFactors: [MfaFactor.TOTP],
            skippable: false,
          };
          return startTotpBinding(flowState, false, userId);
        }
        case MfaFactor.WebAuthn: {
          const factors = userData?.availableFactors ?? [];
          const hasWebAuthn = factors.includes(MfaFactor.WebAuthn);

          if (hasWebAuthn) {
            // Existing passkey → verify with it.
            await startWebAuthnProcessing(
              UserMfaFlow.MfaVerification,
              { availableFactors: [MfaFactor.WebAuthn], skippable: false },
              false,
              userId
            );
            break;
          }

          if (factors.length === 0) {
            // No factors at all (e.g. authenticator was removed) → there is
            // nothing to verify first; bind the passkey directly via the
            // `user.missing_mfa` path, same as the TOTP zero-factor branch.
            await startWebAuthnProcessing(
              UserMfaFlow.MfaBinding,
              { availableFactors: [MfaFactor.WebAuthn], skippable: false },
              false,
              userId
            );
            break;
          }

          // No passkey yet but another factor exists → enroll one
          // (verify-then-enroll). Binding a new factor mid-sign-in is blocked
          // until an existing factor is verified (backend
          // `verifiedInteractionGuard`), and a valid trusted-device cookie
          // would have auto-submitted before this screen — so verify the
          // existing Authenticator (TOTP) first, then resume passkey binding
          // (handled in use-send-mfa-payload via pendingPasskeyEnrollment).
          await handleMfaRedirect(UserMfaFlow.MfaVerification, {
            availableFactors: [MfaFactor.TOTP],
            skippable: false,
            pendingPasskeyEnrollment: true,
          });
          break;
        }
        default: {
          // Handle other MFA factors (WebAuthn, BackupCode, etc.)
          const factors = userData?.availableFactors ?? [];
          const [_, data] = validate(factors, mfaFactors);
          await handleMfaRedirect(UserMfaFlow.MfaVerification, {
            availableFactors: data ?? [],
            skippable: false,
          });
        }
      }
    },
    [
      userData,
      handleMfaRedirect,
      onSubmit,
      userId,
      startTotpBinding,
      startWebAuthnProcessing,
      startReverseWhatsApp,
    ]
  );

  const handleSendPhone = useCallback(async () => {
    if (!phoneInputMode) {
      return;
    }
    const trimmed = phoneValue.trim();
    if (!/^\+?\d{8,15}$/.test(trimmed)) {
      setPhoneError('Enter a valid phone number: 8–15 digits, optional leading +.');
      return;
    }
    setPhoneError(undefined);

    // Reverse-OTP path (WhatsApp only): no billable send — open a wa.me link and poll instead.
    // Await the config load (not the `reverseEnabled` state) so a fast first submit can't race
    // ahead of it and wrongly fall through to the forward-OTP code page — same fix as the
    // already-registered-phone branch in handleMfaSelection.
    if (phoneInputMode === 'whatsapp') {
      const isReverse = await (reverseConfigPromise.current ??
        getWhatsAppConfig()
          .then((config) => config.reverseOtpEnabled)
          .catch(() => false));
      if (isReverse) {
        await startReverseWhatsApp(trimmed);
        return;
      }
    }

    setIsSubmitting(true);

    await onSubmit({
      identifier: SignInIdentifier.Phone,
      value: trimmed,
      userId,
      channel: phoneInputMode === 'whatsapp' ? 'whatsapp' : undefined,
    });

    setIsSubmitting(false);
  }, [phoneInputMode, phoneValue, onSubmit, userId, startReverseWhatsApp]);

  // Show rate limit error if present
  if (isRateLimited()) {
    const retryTime = getRateLimitRetryTime();
    const retryMinutes = retryTime ? Math.ceil(retryTime / 60_000) : 10;

    return (
      <SecondaryPageLayout
        title="mfa.verify_identity"
        description="mfa.verify_identity_description"
      >
        <div className={styles.errorContainer}>
          <h3>Too Many Attempts</h3>
          <p>You&apos;ve made too many attempts. Please try again in {retryMinutes} minutes.</p>
        </div>
      </SecondaryPageLayout>
    );
  }

  // De-dup the header: the generic "select a method" description is only relevant on the factor
  // list. Once a specific panel (WhatsApp link / phone input) is shown, hide it so the page isn't
  // showing two stacked title+description blocks saying the same thing.
  const showGenericDescription = !reverseWaMeUrl && !phoneInputMode;

  return (
    <SecondaryPageLayout
      title="mfa.verify_identity"
      description={showGenericDescription ? 'mfa.verify_identity_description' : ''}
    >
      {isSubmitting || isLoading ? (
        <ClipLoader
          color={color}
          loading={isSubmitting}
          size={30}
          aria-label="Loading Spinner"
          data-testid="loader"
          cssOverride={override}
        />
      ) : reverseWaMeUrl ? (
        <div className={styles.phoneInputPanel}>
          <h3>Verify via WhatsApp</h3>
          {reverseExpired ? (
            <p className={styles.expiredNote}>
              This link has expired. Please go back and request a new one.
            </p>
          ) : (
            <>
              <p>
                {isMobile
                  ? 'Tap the button below to open WhatsApp and send the pre-filled message. This page updates automatically once we receive it.'
                  : 'Scan the QR code with WhatsApp on your phone and send the pre-filled message. This page updates automatically once we receive it.'}
              </p>
              {isMobile ? (
                <a
                  className={styles.openWhatsAppLink}
                  href={reverseWaMeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button title="Open WhatsApp" type="primary" size="large" />
                </a>
              ) : (
                <>
                  <div className={styles.qrFrame}>
                    <QrCode value={reverseWaMeUrl} />
                  </div>
                  <p className={styles.qrCaption}>Scan with your phone camera or WhatsApp</p>
                </>
              )}
              <div className={styles.waitingStatus}>
                <ClipLoader color="currentColor" size={14} />
                <span>Waiting for your message…</span>
              </div>
              {reverseSecondsLeft !== undefined && (
                <p className={styles.expiryNote}>
                  {`Link expires in ${Math.floor(reverseSecondsLeft / 60)}:${String(
                    reverseSecondsLeft % 60
                  ).padStart(2, '0')}`}
                </p>
              )}
            </>
          )}
          <TextLink
            text="action.back"
            onClick={() => {
              stopReverseTimers();
              setReverseWaMeUrl(undefined);
              setReverseExpired(false);
              setReverseSecondsLeft(undefined);
              setPhoneInputMode(null);
              setPhoneValue('');
            }}
          />
        </div>
      ) : phoneInputMode ? (
        <div className={styles.phoneInputPanel}>
          <h3>
            {phoneInputMode === 'whatsapp' ? 'Enter WhatsApp number' : 'Enter phone number for SMS'}
          </h3>
          <p>
            {phoneInputMode === 'whatsapp'
              ? reverseEnabled
                ? "We'll open WhatsApp so you can send us a quick message to verify this number."
                : "We'll send a verification code to your WhatsApp."
              : "We'll send a verification code via SMS."}
          </p>
          <InputField
            autoFocus
            name="phone"
            type="tel"
            placeholder="+62812xxxxxxxx"
            value={phoneValue}
            errorMessage={phoneError}
            isDanger={Boolean(phoneError)}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setPhoneValue(event.target.value);
              if (phoneError) {
                setPhoneError(undefined);
              }
            }}
          />
          <Button
            title="action.continue"
            type="primary"
            isLoading={isSubmitting}
            isDisabled={phoneValue.trim().length === 0}
            onClick={handleSendPhone}
          />
          <TextLink
            text="action.back"
            onClick={() => {
              setPhoneInputMode(null);
              setPhoneValue('');
              setPhoneError(undefined);
            }}
          />
        </div>
      ) : (
        <div className={styles.factorList}>
          {mfaOptions.length === 0 && (
            <p>
              No verification method is currently available. Please contact your administrator.
            </p>
          )}
          {mfaOptions.map((option) => (
            <MfaVerifyButton
              key={option.factor}
              isBinding
              factor={option.factor}
              name={option.name}
              description={option.description}
              onClick={async () => {
                await handleMfaSelection(option);
              }}
            />
          ))}
          {!showAllOptions && mfaOptions.length === 1 && (
            <TextLink
              text={
                (userData?.availableFactors.length ?? 0) === 0
                  ? 'mfa.link_another_mfa_factor'
                  : 'mfa.try_another_verification_method'
              }
              onClick={() => {
                setShowAllOptions(true);
              }}
            />
          )}
        </div>
      )}
      {sessionRemainingMs !== undefined && sessionRemainingMs > 0 && (
        <div
          className={
            sessionRemainingMs <= countdownWarningMs
              ? `${styles.sessionCountdown} ${styles.warning}`
              : styles.sessionCountdown
          }
        >
          {t('mfa.session_countdown', { time: formatCountdown(sessionRemainingMs) })}
        </div>
      )}
    </SecondaryPageLayout>
  );
};

export default MfaChoose;
/* eslint-enable max-lines */
